import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { useNow } from '../hooks/useNow'
import {
  clampTaxRate,
  loadTaxRates,
  saveTaxRatesWithTimestamp,
  taxUpdatedAt,
  type TaxRates,
} from '../lib/taxRates'

type PeriodKind = 'days' | 'current_month'
type PeriodOption = { label: string; value: '7d' | '30d' | '90d' | 'cm' }

const PERIODS: PeriodOption[] = [
  { label: '7 días', value: '7d' },
  { label: '30 días', value: '30d' },
  { label: '90 días', value: '90d' },
  { label: 'Mes actual', value: 'cm' },
]

function periodToParams(value: PeriodOption['value']): {
  days?: number
  currentMonth?: boolean
} {
  switch (value) {
    case '7d':
      return { days: 7 }
    case '30d':
      return { days: 30 }
    case '90d':
      return { days: 90 }
    case 'cm':
      return { currentMonth: true }
  }
}

function applyTax(gross: number, ratePct: number) {
  const rate = ratePct / 100
  return {
    gross,
    tax: gross * rate,
    net: gross * (1 - rate),
  }
}

export function Tax() {
  const [periodValue, setPeriodValue] = useState<PeriodOption['value']>('30d')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const params = useMemo(() => periodToParams(periodValue), [periodValue])
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['tax-preview', periodValue, forceRefetchKey],
    queryFn: () => api.taxPreview(params, force),
  })

  // Tasas personales — viven en localStorage del navegador.
  const [rates, setRates] = useState<TaxRates>(() => loadTaxRates())
  const [editedRates, setEditedRates] = useState<TaxRates>(rates)
  const [updatedAt, setUpdatedAt] = useState<number | null>(() => taxUpdatedAt())
  const [savedFlash, setSavedFlash] = useState(false)

  // Si el user cambia las tasas en otro tab, sincronizamos.
  useEffect(() => {
    const fresh = loadTaxRates()
    setRates(fresh)
    setEditedRates(fresh)
  }, [])

  function onSaveRates() {
    const now = Date.now()
    saveTaxRatesWithTimestamp(editedRates, now)
    setRates(editedRates)
    setUpdatedAt(now)
    setSavedFlash(true)
    window.setTimeout(() => setSavedFlash(false), 1500)
  }

  function onDiscardRates() {
    setEditedRates(rates)
  }

  // dirty = hay cambios sin guardar en las tasas. Cuando hay dirty, los
  // CategoryCards abajo deben usar `editedRates` (no `rates`) para que el
  // preview refleje lo que vas a guardar.
  const ratesDirty =
    editedRates.mining !== rates.mining ||
    editedRates.refining !== rates.refining ||
    editedRates.reactions !== rates.reactions

  // Cálculo de tax + net por categoría y total — usa `effective` que es
  // editedRates si dirty, si no rates. Así el preview refleja los cambios
  // en vivo (sin pedirle al backend que rebuilde).
  const effective = ratesDirty ? editedRates : rates

  const calc = useMemo(() => {
    const mining = data?.mining
      ? applyTax(data.mining.gross_value, effective.mining)
      : { gross: 0, tax: 0, net: 0 }
    const refining = data?.refining
      ? applyTax(data.refining.gross_value, effective.refining)
      : { gross: 0, tax: 0, net: 0 }
    const reactions = data?.reactions
      ? applyTax(data.reactions.gross_value, effective.reactions)
      : { gross: 0, tax: 0, net: 0 }
    const totalGross = mining.gross + refining.gross + reactions.gross
    const totalTax = mining.tax + refining.tax + reactions.tax
    const totalNet = mining.net + refining.net + reactions.net
    return { mining, refining, reactions, totalGross, totalTax, totalNet }
  }, [data, effective])

  const periodLabel = useMemo(() => {
    if (!data?.period) return ''
    if (data.period.kind === 'current_month') return 'Mes actual'
    return `Últimos ${data.period.days ?? 30} días`
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Tax</h1>
          <p className="text-eve-muted text-sm">
            Tu impuesto sobre mining, refining y reactions.
          </p>
        </div>
        {data && (
          <div className="text-right">
            <div className="stat-label">Total bruto</div>
            <div className="stat">{formatISKShort(calc.totalGross)} ISK</div>
            <div className="text-xs text-eve-muted mt-0.5">
              Impuesto {formatISKShort(calc.totalTax)} ISK · Neto{' '}
              {formatISKShort(calc.totalNet)} ISK
            </div>
            <div className="mt-0.5 flex items-center justify-end gap-2">
              <LastUpdated timestamp={dataUpdatedAt} label="" />
              <RefreshButton onClick={bump} isFetching={isFetching} />
            </div>
          </div>
        )}
      </div>

      <RatesCard
        rates={rates}
        edited={editedRates}
        dirty={ratesDirty}
        onChange={setEditedRates}
        onSave={onSaveRates}
        onDiscard={onDiscardRates}
        updatedAt={updatedAt}
        savedFlash={savedFlash}
      />

      {ratesDirty && (
        <div
          role="status"
          className="text-xs text-eve-accent border border-eve-accent/30 bg-eve-accent/10 rounded-md px-3 py-2 flex items-center justify-between gap-2"
        >
          <span>Tenés cambios sin guardar en las tasas. El preview abajo ya los refleja.</span>
          <div className="flex items-center gap-2">
            <button
              onClick={onDiscardRates}
              className="text-xs px-2 py-0.5 rounded border border-eve-border text-eve-muted hover:text-eve-text transition-colors"
            >
              Descartar
            </button>
            <button
              onClick={onSaveRates}
              className="text-xs px-2 py-0.5 rounded border border-eve-accent text-eve-accent hover:bg-eve-accent/10 transition-colors"
            >
              Guardar
            </button>
          </div>
        </div>
      )}

      <PeriodSelector value={periodValue} onChange={setPeriodValue} />

      {data && (
        <div className="text-eve-muted text-xs">
          {periodLabel} · ventana {data.period.start_date.slice(0, 10)} →{' '}
          {data.period.end_date.slice(0, 10)}
        </div>
      )}

      {isLoading ? (
        <LoadingSkeleton variant="rows" rows={6} />
      ) : !data ? (
        <div className="text-eve-muted py-8 text-center">Sin datos.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CategoryCard
              title="Mining"
              gross={data.mining.gross_value}
              tax={calc.mining.tax}
              net={calc.mining.net}
              ratePct={effective.mining}
              empty={data.mining.entries.length === 0}
            >
              <MiningTable entries={data.mining.entries} ratePct={effective.mining} />
            </CategoryCard>
            <CategoryCard
              title="Refining"
              gross={data.refining.gross_value}
              tax={calc.refining.tax}
              net={calc.refining.net}
              ratePct={effective.refining}
              empty={data.refining.jobs.length === 0}
            >
              <JobsTable
                jobs={data.refining.jobs}
                ratePct={effective.refining}
                blueprintCol
              />
            </CategoryCard>
            <CategoryCard
              title="Reactions"
              gross={data.reactions.gross_value}
              tax={calc.reactions.tax}
              net={calc.reactions.net}
              ratePct={effective.reactions}
              empty={data.reactions.jobs.length === 0}
            >
              <JobsTable
                jobs={data.reactions.jobs}
                ratePct={effective.reactions}
                blueprintCol
              />
            </CategoryCard>
          </div>

          <TopContributors
            mining={data.mining}
            refining={data.refining}
            reactions={data.reactions}
            effective={effective}
          />
        </>
      )}
    </div>
  )
}

/**
 * Top contributors — dónde se va tu tax en este período. Agrega
 * mining + refining + reactions por item (type_id para mining,
 * product_type_id para jobs) y muestra los top 10 rankeados por tax
 * pagado. Sin backend change: usa los entries/jobs que ya devuelve
 * /api/tax/preview.
 *
 * Cada "contributor" es la suma de valor para un mismo producto a través
 * de todas las sesiones/jobs del período. Útil para responder
 *   "estoy pagando mucho tax en este producto → ¿lo reproceso en otra
 *    structure con menos tax?" o "¿me conviene cortar este ore?".
 */
function TopContributors({
  mining,
  refining,
  reactions,
  effective,
}: {
  mining: {
    gross_value: number
    entries: Array<{
      date: string
      type_id: number
      name: string
      quantity: number
      unit_price: number | null
      total_value: number | null
    }>
  }
  refining: {
    gross_value: number
    jobs: Array<{
      job_id: number
      blueprint_type_id: number
      blueprint_name: string
      product_type_id: number
      product_name: string
      runs: number
      total_value: number | null
    }>
  }
  reactions: {
    gross_value: number
    jobs: Array<{
      job_id: number
      blueprint_type_id: number
      blueprint_name: string
      product_type_id: number
      product_name: string
      runs: number
      total_value: number | null
    }>
  }
  effective: { mining: number; refining: number; reactions: number }
}) {
  type Contributor = {
    source: 'mining' | 'refining' | 'reactions'
    type_id: number
    name: string
    sub?: string
    gross_value: number
    tax_paid: number
  }

  const contributors: Contributor[] = []

  // Mining: agregamos por type_id (ore name).
  const miningByType = new Map<number, { name: string; gross: number }>()
  for (const e of mining.entries) {
    if (e.total_value === null) continue
    const ex = miningByType.get(e.type_id) ?? { name: e.name, gross: 0 }
    ex.gross += e.total_value
    miningByType.set(e.type_id, ex)
  }
  for (const [typeId, { name, gross }] of miningByType) {
    contributors.push({
      source: 'mining',
      type_id: typeId,
      name,
      gross_value: gross,
      tax_paid: gross * (effective.mining / 100),
    })
  }

  // Refining: agregamos por product_type_id. El BP puede ser distinto pero
  // el producto es lo que se taxa.
  const refiningByProduct = new Map<number, { name: string; gross: number }>()
  for (const j of refining.jobs) {
    if (j.total_value === null) continue
    const ex = refiningByProduct.get(j.product_type_id) ?? {
      name: j.product_name,
      gross: 0,
    }
    ex.gross += j.total_value
    refiningByProduct.set(j.product_type_id, ex)
  }
  for (const [typeId, { name, gross }] of refiningByProduct) {
    contributors.push({
      source: 'refining',
      type_id: typeId,
      name,
      gross_value: gross,
      tax_paid: gross * (effective.refining / 100),
    })
  }

  // Reactions: mismo approach.
  const reactionsByProduct = new Map<number, { name: string; gross: number }>()
  for (const j of reactions.jobs) {
    if (j.total_value === null) continue
    const ex = reactionsByProduct.get(j.product_type_id) ?? {
      name: j.product_name,
      gross: 0,
    }
    ex.gross += j.total_value
    reactionsByProduct.set(j.product_type_id, ex)
  }
  for (const [typeId, { name, gross }] of reactionsByProduct) {
    contributors.push({
      source: 'reactions',
      type_id: typeId,
      name,
      gross_value: gross,
      tax_paid: gross * (effective.reactions / 100),
    })
  }

  if (contributors.length === 0) {
    return null
  }

  contributors.sort((a, b) => b.tax_paid - a.tax_paid)
  const top = contributors.slice(0, 10)
  const totalTax = contributors.reduce((s, c) => s + c.tax_paid, 0)

  return (
    <div className="card">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="stat-label">Top contributors — dónde se va tu tax</div>
          <div className="text-eve-muted text-xs mt-0.5">
            Top 10 rankeados por tax pagado. Suma bruta agregada a través
            de todas las sesiones/jobs del período.
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-eve-muted">Tax total del período</div>
          <div className="font-mono text-sm text-eve-text">
            {formatISKShort(totalTax)} ISK
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-eve-muted border-b border-eve-border">
              <th className="py-2 font-medium w-8">#</th>
              <th className="py-2 font-medium">Source</th>
              <th className="py-2 font-medium">Item</th>
              <th className="py-2 font-medium text-right">Bruto</th>
              <th className="py-2 font-medium text-right">Tax</th>
              <th className="py-2 font-medium text-right">% del tax</th>
            </tr>
          </thead>
          <tbody>
            {top.map((c, idx) => {
              const pct = totalTax > 0 ? (c.tax_paid / totalTax) * 100 : 0
              return (
                <tr
                  key={`${c.source}-${c.type_id}`}
                  className="border-b border-eve-border/50 hover:bg-eve-panel/40 transition-colors"
                >
                  <td className="py-2 text-eve-muted font-mono text-xs">{idx + 1}</td>
                  <td className="py-2">
                    <SourcePill source={c.source} />
                  </td>
                  <td className="py-2 font-medium">{c.name}</td>
                  <td className="py-2 text-right font-mono">
                    {formatISK(c.gross_value, 0)}
                  </td>
                  <td className="py-2 text-right font-mono text-eve-danger">
                    {formatISK(c.tax_paid, 0)}
                  </td>
                  <td className="py-2 text-right font-mono text-eve-muted">
                    {pct.toFixed(1)}%
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {contributors.length > 10 && (
        <div className="text-xs text-eve-muted mt-2 italic">
          + {contributors.length - 10} contributors más (no se muestran).
        </div>
      )}
    </div>
  )
}

function SourcePill({ source }: { source: 'mining' | 'refining' | 'reactions' }) {
  const cfg = (() => {
    switch (source) {
      case 'mining':
        return {
          label: 'Mining',
          classes: 'bg-eve-success/10 border-eve-success/30 text-eve-success',
        }
      case 'refining':
        return {
          label: 'Refining',
          classes: 'bg-eve-accent/10 border-eve-accent/30 text-eve-accent',
        }
      case 'reactions':
        return {
          label: 'Reactions',
          classes: 'bg-eve-danger/10 border-eve-danger/30 text-eve-danger',
        }
    }
  })()
  return (
    <span
      className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${cfg.classes}`}
    >
      {cfg.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

function PeriodSelector({
  value,
  onChange,
}: {
  value: PeriodOption['value']
  onChange: (v: PeriodOption['value']) => void
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {PERIODS.map((p) => {
        const isActive = p.value === value
        return (
          <button
            key={p.value}
            onClick={() => onChange(p.value)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              isActive
                ? 'bg-eve-accent/10 border-eve-accent text-eve-accent'
                : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
            }`}
          >
            {p.label}
          </button>
        )
      })}
    </div>
  )
}

function RatesCard({
  rates,
  edited,
  dirty,
  onChange,
  onSave,
  onDiscard,
  updatedAt,
  savedFlash,
}: {
  rates: TaxRates
  edited: TaxRates
  dirty: boolean
  onChange: (r: TaxRates) => void
  onSave: () => void
  onDiscard: () => void
  updatedAt: number | null
  savedFlash: boolean
}) {
  const now = useNow()

  return (
    <div className="card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="stat-label">Tasas personales</div>
          <div className="text-eve-muted text-xs">
            Se guardan en este navegador (no son del corp).
          </div>
        </div>
        <div className="text-xs text-eve-muted flex items-center gap-2">
          {updatedAt && (
            <span>Última edición: {formatRelative(updatedAt, now)}</span>
          )}
          {savedFlash && (
            <span className="text-eve-success">✓ Guardado</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RateInput
          label="Mining"
          value={edited.mining}
          onChange={(v) => onChange({ ...edited, mining: v })}
        />
        <RateInput
          label="Refining"
          value={edited.refining}
          onChange={(v) => onChange({ ...edited, refining: v })}
        />
        <RateInput
          label="Reactions"
          value={edited.reactions}
          onChange={(v) => onChange({ ...edited, reactions: v })}
        />
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          onClick={onDiscard}
          disabled={!dirty}
          className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Descartar
        </button>
        <button
          onClick={onSave}
          disabled={!dirty}
          className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Guardar tasas
        </button>
      </div>
    </div>
  )
}

function RateInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-eve-muted text-xs">{label} (%)</span>
      <input
        type="number"
        min={0}
        max={100}
        step={0.5}
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value)
          // Misma semántica que clampTaxRate del helper, así no drift
          // entre lo que acepta el UI y lo que se guarda en localStorage.
          onChange(Number.isFinite(n) ? clampTaxRate(n) : 0)
        }}
        className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none font-mono"
      />
    </label>
  )
}

function CategoryCard({
  title,
  gross,
  tax,
  net,
  ratePct,
  empty,
  children,
}: {
  title: string
  gross: number
  tax: number
  net: number
  ratePct: number
  empty: boolean
  children: React.ReactNode
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="stat-label">{title}</div>
          <div className="text-eve-muted text-xs">Tasa: {ratePct.toFixed(1)}%</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg">{formatISKShort(gross)}</div>
          <div className="text-xs text-eve-muted">
            tax {formatISKShort(tax)} · net {formatISKShort(net)}
          </div>
        </div>
      </div>
      <details className="mt-2">
        <summary className="cursor-pointer text-eve-muted text-xs hover:text-eve-text">
          {empty ? 'Sin actividad en este período' : 'Ver detalle'}
        </summary>
        <div className="mt-3">{children}</div>
      </details>
    </div>
  )
}

function MiningTable({
  entries,
  ratePct,
}: {
  entries: Array<{
    date: string
    name: string
    quantity: number
    unit_price: number | null
    total_value: number | null
  }>
  ratePct: number
}) {
  if (entries.length === 0)
    return (
      <div className="text-eve-muted text-sm">Sin actividad.</div>
    )
  const rate = ratePct / 100
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-eve-muted border-b border-eve-border">
            <th className="py-2 font-medium">Fecha</th>
            <th className="py-2 font-medium">Mineral</th>
            <th className="py-2 font-medium text-right">Qty</th>
            <th className="py-2 font-medium text-right">Unit</th>
            <th className="py-2 font-medium text-right">Bruto</th>
            <th className="py-2 font-medium text-right">Tax</th>
            <th className="py-2 font-medium text-right">Neto</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const v = e.total_value
            const taxVal = v !== null ? v * rate : null
            const netVal = v !== null ? v * (1 - rate) : null
            return (
              <tr key={`${e.date}-${i}`} className="border-b border-eve-border/50">
                <td className="py-1.5">{e.date.slice(0, 10)}</td>
                <td className="py-1.5">{e.name}</td>
                <td className="py-1.5 text-right font-mono">
                  {e.quantity.toLocaleString('en-US')}
                </td>
                <td className="py-1.5 text-right font-mono text-eve-muted">
                  {e.unit_price !== null ? formatISK(e.unit_price, 0) : '—'}
                </td>
                <td className="py-1.5 text-right font-mono">
                  {v !== null ? formatISK(v, 0) : '—'}
                </td>
                <td className="py-1.5 text-right font-mono text-eve-muted">
                  {taxVal !== null ? formatISK(taxVal, 0) : '—'}
                </td>
                <td className="py-1.5 text-right font-mono text-eve-muted">
                  {netVal !== null ? formatISK(netVal, 0) : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function JobsTable({
  jobs,
  ratePct,
  blueprintCol,
}: {
  jobs: Array<{
    job_id: number
    end_date: string
    blueprint_name: string
    product_name: string
    runs: number
    unit_price: number | null
    total_value: number | null
  }>
  ratePct: number
  blueprintCol?: boolean
}) {
  if (jobs.length === 0)
    return (
      <div className="text-eve-muted text-sm">Sin actividad.</div>
    )
  const rate = ratePct / 100
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-eve-muted border-b border-eve-border">
            <th className="py-2 font-medium">Fecha</th>
            {blueprintCol && <th className="py-2 font-medium">Blueprint</th>}
            <th className="py-2 font-medium">Output</th>
            <th className="py-2 font-medium text-right">Runs</th>
            <th className="py-2 font-medium text-right">Unit</th>
            <th className="py-2 font-medium text-right">Bruto</th>
            <th className="py-2 font-medium text-right">Tax</th>
            <th className="py-2 font-medium text-right">Neto</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => {
            const v = j.total_value
            const taxVal = v !== null ? v * rate : null
            const netVal = v !== null ? v * (1 - rate) : null
            return (
              <tr key={j.job_id} className="border-b border-eve-border/50">
                <td className="py-1.5">{j.end_date.slice(0, 10)}</td>
                {blueprintCol && (
                  <td className="py-1.5 text-eve-muted">{j.blueprint_name}</td>
                )}
                <td className="py-1.5">{j.product_name}</td>
                <td className="py-1.5 text-right font-mono">{j.runs}</td>
                <td className="py-1.5 text-right font-mono text-eve-muted">
                  {j.unit_price !== null ? formatISK(j.unit_price, 0) : '—'}
                </td>
                <td className="py-1.5 text-right font-mono">
                  {v !== null ? formatISK(v, 0) : '—'}
                </td>
                <td className="py-1.5 text-right font-mono text-eve-muted">
                  {taxVal !== null ? formatISK(taxVal, 0) : '—'}
                </td>
                <td className="py-1.5 text-right font-mono text-eve-muted">
                  {netVal !== null ? formatISK(netVal, 0) : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function formatRelative(ts: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - ts) / 1000))
  if (seconds < 5) return 'ahora'
  if (seconds < 60) return `hace ${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `hace ${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `hace ${hours}h`
  const days = Math.floor(hours / 24)
  return `hace ${days}d`
}